import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import { Kicker } from "@/components/Primitives";

const KPIS = [
  ["assigned_candidates", "stats.assigned"],
  ["new_assigned_last_7_days", "stats.new7"],
  ["open_follow_ups", "stats.openTasks"],
  ["overdue_follow_ups", "stats.overdue"],
];

export default function RecruiterStats() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/recruiter-stats").then(({ data }) => setData(data)).catch(() => setData(false));
  }, []);

  if (!data) return <p className="text-sm text-muted-foreground">{t("common.loading")}</p>;

  return (
    <div data-testid="recruiter-stats">
      <Kicker>{t("stats.scope")}</Kicker>
      <h1 className="mt-3 font-display text-3xl font-light sm:text-4xl">{t("stats.title")}</h1>

      <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {KPIS.map(([key, label]) => (
          <div key={key} data-testid={`stats-${key}`} className="border border-border bg-surface p-5">
            <p className="cs-kicker">{t(label)}</p>
            <p className="mt-3 font-sans text-4xl font-light tabular-nums">{data[key] ?? 0}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <section data-testid="stats-by-stage" className="lg:col-span-5 border border-border bg-surface p-5">
          <h2 className="font-display text-xl">{t("admin.byStage")}</h2>
          {Object.keys(data.by_stage || {}).length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">{t("admin.noResults")}</p>
          ) : (
            <ul className="mt-4 space-y-2 text-sm">
              {Object.entries(data.by_stage).map(([stage, count]) => (
                <li key={stage} className="flex justify-between gap-3">
                  <span className="text-muted-foreground">{t(`stages.${stage}`)}</span>
                  <span className="tabular-nums">{count}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section data-testid="stats-activity" className="lg:col-span-3 border border-border bg-surface p-5">
          <h2 className="font-display text-xl">{t("stats.activity")}</h2>
          <ul className="mt-4 space-y-2 text-sm">
            <li className="flex justify-between gap-3">
              <span className="text-muted-foreground">{t("stats.moves7")}</span>
              <span className="tabular-nums">{data.stage_movements_last_7_days}</span>
            </li>
            <li className="flex justify-between gap-3">
              <span className="text-muted-foreground">{t("stats.moves30")}</span>
              <span className="tabular-nums">{data.stage_movements_last_30_days}</span>
            </li>
            <li className="flex justify-between gap-3">
              <span className="text-muted-foreground">{t("stats.notes30")}</span>
              <span className="tabular-nums">{data.notes_last_30_days}</span>
            </li>
            <li className="flex justify-between gap-3">
              <span className="text-muted-foreground">{t("stats.claimed")}</span>
              <span className="tabular-nums">{data.candidates_claimed}</span>
            </li>
          </ul>
        </section>

        <section data-testid="stats-tasks" className="lg:col-span-4 border border-border bg-surface p-5">
          <h2 className="font-display text-xl">{t("admin.tasksDue")}</h2>
          {(data.tasks_due || []).length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">{t("admin.noTasks")}</p>
          ) : (
            <ul className="mt-4 divide-y divide-border text-sm">
              {data.tasks_due.map((task) => (
                <li key={task.id} className="py-2">
                  <Link to={`/${lang}/admin/candidates/${task.candidate_id}`} className="hover:text-accent">
                    {task.title}
                  </Link>
                  <span className="ml-2 text-xs text-muted-foreground">{task.due_date || "—"}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section data-testid="stats-recent-moves" className="lg:col-span-12 border border-border bg-surface p-5">
          <h2 className="font-display text-xl">{t("stats.recentMoves")}</h2>
          {(data.recent_stage_movements || []).length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">{t("admin.noResults")}</p>
          ) : (
            <ul className="mt-4 divide-y divide-border text-sm">
              {data.recent_stage_movements.map((move) => (
                <li key={move.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <Link to={`/${lang}/admin/candidates/${move.candidate_id}`} className="hover:text-accent">
                    {move.from_stage ? t(`stages.${move.from_stage}`) : "—"} → {t(`stages.${move.to_stage}`)}
                  </Link>
                  <span className="text-xs text-muted-foreground">
                    {(move.created_at || "").slice(0, 16).replace("T", " ")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
